import { useEffect, useState, useCallback } from "react";
import { Plus, Edit2, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { title: "", slug: "", cover_image_url: "", excerpt: "", content_md: "", author: "Healthink Editorial", category_id: "", published: false };

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function AdminBlogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filterPublished, setFilterPublished] = useState("all");
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [slugEdited, setSlugEdited] = useState(false);
  const [openForm, setOpenForm] = useState(false);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: "30" });
    if (q) p.set("q", q);
    if (filterPublished !== "all") p.set("published", filterPublished);
    const { data } = await api.get(`/admin/blogs?${p.toString()}`);
    setItems(data.items); setTotal(data.total);
  }, [q, filterPublished, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setSlugEdited(false); setOpenForm(true); };
  const openEdit = async (b) => {
    const { data } = await api.get(`/blogs/${b.slug}`).catch(async () => {
      // Drafts not exposed via /blogs/:slug — fetch via admin list with content (re-query)
      return { data: b };
    });
    setEditing(b.id);
    setForm({
      title: data.title, slug: data.slug, cover_image_url: data.cover_image_url,
      excerpt: data.excerpt || "", content_md: data.content_md || b.content_md || "",
      author: data.author, category_id: data.category_id || "", published: !!data.published,
    });
    setSlugEdited(true);
    setOpenForm(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, category_id: form.category_id || null };
      if (editing) await api.patch(`/admin/blogs/${editing}`, payload);
      else await api.post("/admin/blogs", payload);
      toast.success(editing ? "Blog updated" : "Blog created");
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const togglePublish = async (b) => {
    try {
      const { data } = await api.get(`/blogs/${b.slug}`).catch(() => ({ data: null }));
      const content_md = data?.content_md || b.content_md || "";
      await api.patch(`/admin/blogs/${b.id}`, {
        title: b.title, slug: b.slug, cover_image_url: b.cover_image_url,
        excerpt: b.excerpt, content_md, author: b.author,
        category_id: b.category_id || null, published: !b.published,
      });
      toast.success(b.published ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onTitleChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, title: v, slug: slugEdited ? f.slug : slugify(v) }));
  };

  return (
    <AdminLayout title="Blogs" subtitle={`${total} article${total === 1 ? "" : "s"}`} action={
      <Button data-testid="admin-add-blog-btn" onClick={openCreate} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Add Blog</Button>
    }>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input data-testid="blog-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search title/slug" className="w-72 rounded-full" />
        <Select value={filterPublished} onValueChange={(v) => { setFilterPublished(v); setPage(1); }}>
          <SelectTrigger data-testid="blog-published-filter" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="true">Published</SelectItem>
            <SelectItem value="false">Drafts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div data-testid="admin-blogs-list" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((b) => (
          <div key={b.id} data-testid={`blog-card-${b.id}`} className="bg-white border border-border rounded-2xl overflow-hidden flex">
            <img src={b.cover_image_url} alt="" className="h-32 w-40 object-cover shrink-0" />
            <div className="p-3 flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{b.title}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">{b.slug}</div>
              <div className="text-xs mt-1 line-clamp-2">{b.excerpt}</div>
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-xs">
                  <Switch data-testid={`blog-publish-${b.id}`} checked={b.published} onCheckedChange={() => togglePublish(b)} />{b.published ? "Published" : "Draft"}
                </label>
                <div>
                  {b.published && (
                    <Button asChild size="sm" variant="ghost" data-testid={`blog-preview-${b.id}`}>
                      <a href={`/patient/blogs/${b.slug}`} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`blog-edit-${b.id}`} onClick={() => openEdit(b)}><Edit2 className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`blog-del-${b.id}`} onClick={() => del(b.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No articles yet.</div>}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="blog-form-modal" className="sm:max-w-5xl rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>{editing ? "Edit Blog" : "Add Blog"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-y-auto px-1">
            <div className="space-y-3">
              <div className="space-y-1.5"><label className="text-xs font-medium">Title</label>
                <Input data-testid="blog-form-title" value={form.title} onChange={onTitleChange} /></div>
              <div className="space-y-1.5"><label className="text-xs font-medium">Slug</label>
                <Input data-testid="blog-form-slug" value={form.slug} onChange={(e) => { setForm((f) => ({ ...f, slug: slugify(e.target.value) })); setSlugEdited(true); }} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><label className="text-xs font-medium">Category</label>
                  <Select value={form.category_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="blog-form-cat"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— None —</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><label className="text-xs font-medium">Author</label>
                  <Input data-testid="blog-form-author" value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} /></div>
              </div>
              <div className="space-y-1.5"><label className="text-xs font-medium">Cover image URL</label>
                <Input data-testid="blog-form-cover" value={form.cover_image_url} onChange={(e) => setForm((f) => ({ ...f, cover_image_url: e.target.value }))} placeholder="https://..." />
                {form.cover_image_url && <img src={form.cover_image_url} alt="" className="mt-1 w-full h-32 rounded-xl object-cover" />}
              </div>
              <div className="space-y-1.5"><label className="text-xs font-medium">Excerpt <span className="text-muted-foreground">(~150 chars; auto from body if empty)</span></label>
                <Textarea data-testid="blog-form-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} /></div>
              <div className="space-y-1.5"><label className="text-xs font-medium">Content (Markdown)</label>
                <Textarea data-testid="blog-form-md" rows={12} value={form.content_md} onChange={(e) => setForm((f) => ({ ...f, content_md: e.target.value }))} placeholder="## Heading&#10;&#10;Your story…" className="font-mono text-xs" /></div>
              <label className="flex items-center gap-2 text-sm">
                <Switch data-testid="blog-form-published" checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} />Published
              </label>
            </div>
            <div className="bg-secondary/40 rounded-xl p-4 overflow-y-auto">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Live preview</div>
              {form.cover_image_url && <img src={form.cover_image_url} alt="" className="w-full h-32 rounded-xl object-cover mb-3" />}
              <h1 className="text-xl font-bold tracking-tight mb-1">{form.title || "Untitled"}</h1>
              <div className="text-xs text-muted-foreground mb-3">{form.author}</div>
              <div className="prose prose-sm max-w-none" data-testid="blog-form-preview">
                <ReactMarkdown>{form.content_md || "_Start typing to see preview…_"}</ReactMarkdown>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-3 border-t border-border">
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="blog-save-btn" onClick={save} disabled={!form.title || !form.content_md} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {total > 30 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 30)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
